import React, { forwardRef, useState, useMemo, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Form } from '@ant-design/compatible';

import Input from 'common/components/input';
import Modal from 'common/components/modal';
import { emailReg } from 'common/helpers/validate';
import useStyles from '../style';

export const inputField = {
  ACCOUNT_OWNER: 'ACCOUNT_OWNER',
};

const EditModal = forwardRef(
  ({ form, landlord = {}, updateLandlordLoading = false, updateCallback, updateLandlord }, ref) => {
    const { getFieldDecorator, validateFields, resetFields } = form;
    const { t } = useTranslation();
    const classes = useStyles();

    const [visible, setVisible] = useState(false);
    const [field, setField] = useState(null);

    const handleCancelModal = () => {
      setVisible(false);
      resetFields(['accountManager']);
    };

    const handleOpenModal = field => {
      setField(field);
      setVisible(true);
    };

    const handleConfirmModal = () => {
      validateFields((err, values) => {
        if (!err) {
          const params = {
            id: landlord.id,
          };

          if (field === inputField.ACCOUNT_OWNER) {
            params.accountManager = values.accountManager;
          }

          updateLandlord(params, () => {
            handleCancelModal();
            updateCallback();
          });
        }
      });
    };

    const { modalTitle, label } = useMemo(() => {
      switch (field) {
        case inputField.ACCOUNT_OWNER:
          return {
            modalTitle: t('cms.landlord.modal.title.edit_account_owner'),
            label: t('cms.landlord.detail.account_owner.label'),
          };
        case inputField.BOOKING_JOURNEY:
          return {
            modalTitle: t('cms.landlord.modal.title.edit_landlord_type'),
            label: t('cms.landlord.detail.booking_journey.label'),
          };
        default:
          return {
            modalTitle: '',
            label: '',
          };
      }
    }, [field, t]);

    useImperativeHandle(ref, () => ({
      handleOpenModal,
    }));

    return (
      <Modal
        visible={visible}
        title={modalTitle}
        wrapClassName={classes.modalWrapper}
        onCancel={handleCancelModal}
        onOk={handleConfirmModal}
        okButtonProps={{
          loading: updateLandlordLoading,
        }}
        okText={t('cms.landlord.modal.confirm.button')}
      >
        <Form colon={false} className={classes.formWrapper}>
          <Choose>
            <When condition={field === inputField.ACCOUNT_OWNER}>
              <Form.Item label={label}>
                {getFieldDecorator('accountManager', {
                  initialValue: landlord.accountManager,
                  rules: [
                    {
                      required: true,
                      message: t('cms.properties.edit.error.blank'),
                    },
                    {
                      pattern: emailReg,
                      message: t('cms.properties.edit.error.email'),
                    },
                  ],
                })(<Input />)}
              </Form.Item>
            </When>
          </Choose>
        </Form>
      </Modal>
    );
  },
);

EditModal.displayName = 'EditModal';

EditModal.propTypes = {
  form: PropTypes.object.isRequired,
  landlord: PropTypes.object.isRequired,
  updateLandlordLoading: PropTypes.bool.isRequired,
  updateCallback: PropTypes.func.isRequired,
  updateLandlord: PropTypes.func.isRequired,
};

export default Form.create({
  name: 'Edit Modal',
})(EditModal);
